
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface UserProgressData {
  id: string;
  user_id: string;
  node_id: string;
  status: string;
  progress_percentage: number | null;
  time_spent_seconds: number | null;
  completed_at: string | null;
  last_accessed_at: string | null;
  engagement_score: number | null;
}

export interface UserGamificationData {
  id: string;
  user_id: string;
  total_points: number;
  level: number;
  nodes_viewed: number;
  nodes_completed: number;
  content_created: number;
  comments_made: number;
  current_streak: number;
  longest_streak: number;
  last_activity_date: string;
}

export interface UserAchievement {
  id: string;
  user_id: string;
  achievement_type: string;
  achievement_data: any;
  earned_at: string;
  points_awarded: number;
}

export const useUserProgress = (nodeId?: string) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['user-progress', user?.id, nodeId],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      
      let query = supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id);
      
      if (nodeId) {
        query = query.eq('node_id', nodeId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as UserProgressData[];
    },
    enabled: !!user?.id,
  });
};

export const useUserGamification = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['user-gamification', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('user_gamification')
        .select('*')
        .eq('user_id', user.id)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data as UserGamificationData | null;
    },
    enabled: !!user?.id,
  });
};

export const useUserAchievements = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['user-achievements', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('user_achievements')
        .select('*')
        .eq('user_id', user.id)
        .order('earned_at', { ascending: false });
      
      if (error) throw error;
      return data as UserAchievement[];
    },
    enabled: !!user?.id,
  });
};

export const useUpdateUserProgress = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      nodeId, 
      status, 
      timeSpent = 0 
    }: { 
      nodeId: string; 
      status: string; 
      timeSpent?: number; 
    }) => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('update-user-progress', {
        body: {
          nodeId,
          status,
          timeSpent
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Invalidate and refetch progress data
      queryClient.invalidateQueries({ queryKey: ['user-progress'] });
      queryClient.invalidateQueries({ queryKey: ['user-gamification'] });
      queryClient.invalidateQueries({ queryKey: ['user-achievements'] });
      
      // Show achievement notifications
      if (data.recentAchievements && data.recentAchievements.length > 0) {
        const latestAchievement = data.recentAchievements[0];
        const achievementNames = {
          'first_view': 'First View',
          'first_completion': 'First Completion',
          'discipline_explorer': 'Discipline Explorer',
          'active_learner': 'Active Learner',
          'knowledge_seeker': 'Knowledge Seeker',
          'content_creator': 'Content Creator',
          'community_helper': 'Community Helper'
        };
        
        toast.success(`Achievement Unlocked: ${achievementNames[latestAchievement.achievement_type] || latestAchievement.achievement_type}!`, {
          description: `+${latestAchievement.points_awarded} points`
        });
      }
    },
    onError: (error) => {
      console.error('Error updating user progress:', error);
      toast.error('Failed to update progress');
    },
  });
};
